<template>
    <form class="create-form">
        <div v-if="loading" class="absolute z-100 left-0 top-0 w-full h-full flex items-center justify-center">
            <Preloader />
        </div>

        <div class="grid grid-cols-12 gap-6 mt-4">
            <div class="relative col-span-6">
                <label for="form-name" class="form-label">
                    Название
                    <sup v-if="v$.form.name.required" class="text-theme-6">*</sup>
                </label>
                <label v-if="v$.form.name.$errors.length" for="form-name" class="form-input-text-error">
                    {{ getOverrideMessage(v$.form.name.$errors[0].$message) }}
                </label>
                <input
                    id="form-name"
                    v-model="form.name"
                    type="text"
                    class="form-control w-full"
                    :class="{ 'form-input-error': v$.form.name.$errors.length }"
                    placeholder=""
                    autocomplete="off"
                    :data-value="form.name"
                />
            </div>

            <div class="col-span-6">
                <label for="form-category_id" class="form-label">Категория</label>
                <select
                    id="form-category_id"
                    v-model="form.category_id"
                    name="type"
                    class="form-select sm:mr-2"
                    autocomplete="off"
                >
                    <option value="">Нет</option>
                    <option
                        v-for="(category, optIdx) in categories"
                        :key="optIdx"
                        :value="category.id"
                        :selected="form.category_id === category.id"
                    >
                        {{ category.name }}
                    </option>
                </select>
            </div>

            <div class="relative col-span-6">
                <label for="form-header" class="form-label">
                    Заголовок
                    <sup v-if="v$.form.header.required" class="text-theme-6">*</sup>
                </label>
                <label v-if="v$.form.header.$errors.length" for="form-header" class="form-input-text-error">
                    {{ getOverrideMessage(v$.form.header.$errors[0].$message) }}
                </label>
                <input
                    id="form-header"
                    v-model="form.header"
                    type="text"
                    class="form-control w-full"
                    :class="{ 'form-input-error': v$.form.header.$errors.length }"
                    placeholder=""
                    autocomplete="off"
                    :data-value="form.header"
                />
            </div>

            <div class="col-span-6">
                <label for="form-sub_header" class="form-label">Подзаголовок</label>
                <input
                    id="form-sub_header"
                    v-model="form.sub_header"
                    type="text"
                    class="form-control w-full"
                    placeholder=""
                    autocomplete="off"
                    :data-value="form.sub_header"
                />
            </div>

            <div class="relative col-span-4">
                <label for="form-slug" class="form-label">
                    Ссылка
                    <sup v-if="v$.form.slug.required" class="text-theme-6">*</sup>
                </label>
                <label v-if="v$.form.slug.$errors.length" for="form-slug" class="form-input-text-error">
                    {{ getOverrideMessage(v$.form.slug.$errors[0].$message) }}
                </label>
                <input
                    id="form-slug"
                    v-model="form.slug"
                    type="text"
                    class="form-control w-full"
                    :class="{ 'form-input-error': v$.form.slug.$errors.length }"
                    placeholder=""
                    autocomplete="off"
                    :data-value="form.slug"
                />
            </div>

            <div class="col-span-4">
                <label for="form-date" class="form-label">Дата публикации</label>
                <input id="form-date" v-model="form.date" type="date" class="form-control w-full" autocomplete="off" />
            </div>

            <div class="col-span-4">
                <label for="form-published" class="form-label">Опубликовано</label>
                <select id="form-published" v-model="form.published" class="form-select">
                    <option value="1">Опубликовано</option>
                    <option value="0">Не опубликовано</option>
                </select>
            </div>

            <div class="col-span-12">
                <label for="form-faq_ids" class="form-label">Вопросы и ответы</label>
                <Multiselect
                    id="form-faq_ids"
                    v-model="form.faq_ids"
                    mode="tags"
                    placeholder="Выберите несколько вопросов"
                    track-by="question"
                    label="question"
                    value-prop="id"
                    :hide-selected="false"
                    :close-on-select="false"
                    :searchable="true"
                    :options="faq"
                    no-results-text="Не найдено"
                    no-options-text="Не найдено"
                    :native="false"
                >
                    <template #option="{ option }">
                        {{ option.question }}
                    </template>
                </Multiselect>
            </div>

            <div class="relative col-span-12">
                <label class="form-label">Предварительный текст</label>
                <CustomWyswig v-model="form.intro_text" />
            </div>

            <div class="relative col-span-12">
                <label class="form-label">Полный текст</label>
                <CustomWyswig v-model="form.full_text" />
            </div>

            <div class="col-span-12">
                <label class="form-label">Изображение</label>
                <UploadImageNew
                    ref="update_image"
                    v-model="form.image"
                    :delete="form.image_delete"
                    recommended-sizes="1170х300px"
                />
            </div>

            <div class="col-span-12">
                <h4 class="font-bold text-xl mb-3">Seo</h4>
                <label for="form-seo_title" class="form-label">Seo заголовок</label>
                <input
                    id="form-seo_title"
                    v-model="form.seo_title"
                    type="text"
                    class="form-control w-full"
                    placeholder=""
                    autocomplete="off"
                    :data-value="form.seo_title"
                />
            </div>

            <div class="col-span-12">
                <label class="form-label">Seo текст</label>
                <CustomWyswig v-model="form.seo_text" />
            </div>

            <div class="col-span-12" title="SEO тэг для каждого круиза на этой странице">
                <label for="form-hidden_tag" class="form-label">Скрытые тэги для круизов</label>

                <Multiselect
                    id="form-hidden_tag"
                    v-model="form.hidden_tag"
                    :options="form.hidden_tag"
                    mode="tags"
                    placeholder="SEO тэги для каждого круиза на этой странице"
                    :hide-selected="false"
                    :close-on-select="false"
                    :native="false"
                    :searchable="true"
                    :create-option="true"
                    no-results-text="Введите новый тэг и Enter чтобы создать"
                    no-options-text="Введите новый тэг и Enter чтобы создать"
                />
            </div>

            <div class="col-span-12" title="SEO тэг для каждого отзыва на этой странице">
                <label for="form-hidden_tag_review" class="form-label">Скрытые тэги для отзывов</label>

                <Multiselect
                    id="form-hidden_tag_review"
                    v-model="form.hidden_tag_review"
                    :options="form.hidden_tag_review"
                    mode="tags"
                    placeholder="SEO тэги для каждого отзыва на этой странице"
                    :hide-selected="false"
                    :close-on-select="false"
                    :native="false"
                    :searchable="true"
                    :create-option="true"
                    no-results-text="Введите новый тэг и Enter чтобы создать"
                    no-options-text="Введите новый тэг и Enter чтобы создать"
                />
            </div>

            <div class="col-span-12" title="SEO тэг для каждого теплохода на этой странице">
                <label for="form-hidden_tag_ship" class="form-label">Скрытые тэги для теплоходов</label>

                <Multiselect
                    id="form-hidden_tag_ship"
                    v-model="form.hidden_tag_ship"
                    :options="form.hidden_tag_ship"
                    mode="tags"
                    placeholder="SEO тэги для каждого теплохода на этой странице"
                    :hide-selected="false"
                    :close-on-select="false"
                    :native="false"
                    :searchable="true"
                    :create-option="true"
                    no-results-text="Введите новый тэг и Enter чтобы создать"
                    no-options-text="Введите новый тэг и Enter чтобы создать"
                />
            </div>

            <div class="col-span-12">
                <label class="form-label font-bold">Акции</label>
                <p v-if="promotionOptions.length === 0">Отсутствуют</p>
                <MultiselectTable
                    v-else
                    v-model="form.promotions"
                    :header-labels="promotionTableLabels"
                    :options="promotionOptions"
                    :track-by="promotionMultiselectTrackBy"
                />
            </div>

            <div class="col-span-12">
                <label class="form-label font-bold">Кастомные поля</label>
                <TeamsTable v-model="seo" :meta="null" :headers="seoHeaders" @update:modelValue="updateSeo" />
            </div>
        </div>

        <div class="col-span-12 flex items-center justify-end mt-5">
            <router-link :to="{ name: 'pages' }" class="btn btn-secondary w-24 mr-2 mb-2">Отмена</router-link>
            <button type="submit" class="btn btn-primary mb-2 mr-2" @click.prevent="saveAndQuit">
                Сохранить и выйти
            </button>
            <button type="submit" class="btn btn-primary mb-2" @click.prevent="saveAndEdit">Сохранить</button>
        </div>
    </form>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Preloader from '@/components/preloader/Main';
import UploadImageNew from '@/components/upload-image/UploadImageNew.vue';
import CustomWyswig from '@/components/base/custom-wyswig/Main';
import TeamsTable from '@/components/tables/TeamsTable';
import MultiselectTable from '@/components/tables/MultiselectTable';
import { errorResponse } from '@/mixins/form/form-mixin';
import { helper as $h } from '@/utils/helper';
import { formPreparation } from '@/utils/object-to-form-data';
import { pagesMixin } from '@/mixins/form/pages-mixin';
import Multiselect from '@vueform/multiselect';

export default {
    name: 'CreatePage',
    components: { Preloader, Multiselect, UploadImageNew, CustomWyswig, TeamsTable, MultiselectTable },
    mixins: [pagesMixin, errorResponse],
    setup() {
        return { v$: useVuelidate() };
    },
    validations() {
        return {
            form: {
                name: { required },
                header: { required },
                slug: { required },
            },
        };
    },
    async created() {
        this.$store.commit('main/setPageHeader', 'Создать страницу');

        await this.fetchDataForFilling();
    },
    methods: {
        async fetchDataForFilling() {
            this.loading = true;

            try {
                const { data } = await this.axios.get('/pages/create');
                this.categories = data.categories;
                this.promotionOptions = data.promotions;
            } catch (ex) {
                console.log('cant fetch categories: ' + ex);
            } finally {
                this.loading = false;
            }
        },
        async save() {
            const isFormCorrect = await this.v$.$validate();
            if (!isFormCorrect) {
                this.$notify({ text: 'Заполните все поля правильно', type: 'error' });
                return;
            }

            this.loading = true;
            this.form.seo = this.seo;

            try {
                const formData = formPreparation(this.form, { nullsAsUndefineds: false, imagesAsObject: true });
                const { data } = await this.axios.post('/pages', formData);
                this.$notify(data.message);
                this.loading = false;
                return true;
            } catch (ex) {
                this.errorResponse(ex);
                this.loading = false;
                return false;
            }
        },
        async saveAndQuit() {
            const success = await this.save();
            if (success) this.$router.push({ name: 'pages' });
        },
        async saveAndEdit() {
            const success = await this.save();
            if (success) this.$router.push({ name: 'pages', query: { id: data.data.id } });
        },
        getOverrideMessage(message) {
            return $h.getOverrideMessage(message);
        },
    },
};
</script>

<style scoped></style>
